import { useMutation, useQueryClient } from '@tanstack/react-query';
import { financialInstrumentQueryKey } from './keys';
import { normalizeFinancialInstrument } from '../types';
import type {
  CreateFinancialInstrumentPayload,
  FinancialInstrumentDto,
  FinancialInstrumentHttpRequest,
  RawFinancialInstrumentDto,
} from '../types';

export interface UseCreateFinancialInstrumentParams {
  documentId: string;
  /** Endpoint base de child (`/{id}/financial-instrument`). El POST va a esta ruta. */
  financialInstrumentBaseEndpoint: string;
  httpRequest: FinancialInstrumentHttpRequest;
}

/**
 * Crea el Instrumento Financiero (`POST /{id}/financial-instrument`) → 201 + DTO.
 *
 * El backend devuelve el DTO directamente (sin envelope `{child, header}`: el IF
 * es child de tabla propia, no child LEGO de `DocumentCore`). Un 2º IF activo →
 * 422 (BV-S07), que el panel surfacea.
 *
 * - `onSuccess`: siembra el IF en el cache.
 * - `onSettled`: invalida `['financial-instrument', documentId]`.
 */
export function useCreateFinancialInstrument({
  documentId,
  financialInstrumentBaseEndpoint,
  httpRequest,
}: UseCreateFinancialInstrumentParams) {
  const queryClient = useQueryClient();
  const key = financialInstrumentQueryKey(documentId);

  return useMutation<FinancialInstrumentDto, Error, CreateFinancialInstrumentPayload>({
    mutationFn: async (payload) => {
      const res = await httpRequest<RawFinancialInstrumentDto>(
        'POST',
        financialInstrumentBaseEndpoint,
        payload,
      );
      return normalizeFinancialInstrument(res);
    },
    onSuccess: (created) => {
      queryClient.setQueryData<FinancialInstrumentDto | null>(key, created);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
